import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from '../lib/router.tsx';
import { isExternalHref, safeHref } from '../lib/safe-url.ts';
import { VisuallyHidden } from './kit.tsx';

/**
 * The only component that renders <a> (roadmap §3.6; a lint rule forbids <a> elsewhere). Every href passes
 * the allowlist: same-origin paths and https URLs. A refused href renders as plain text, never as a link.
 *
 * - app paths navigate inside the app, unless `document` is set (/auth/* must be real page loads);
 * - external https links open in a new tab with noopener/noreferrer, unless `sameTab`.
 */
type Props = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'target' | 'rel'> & {
  href: string;
  children: ReactNode;
  document?: boolean;
  sameTab?: boolean;
};

export function SafeLink({ href, children, document: fullLoad = false, sameTab = false, onClick, ...rest }: Props) {
  const { t } = useTranslation();
  const { navigate, path } = useRouter();
  const safe = safeHref(href);
  if (safe === null) return <span className={rest.className}>{children}</span>;

  if (isExternalHref(safe)) {
    return sameTab
      ? <a href={safe} rel="noreferrer" {...rest}>{children}</a>
      : (
        <a href={safe} target="_blank" rel="noopener noreferrer" {...rest}>
          {children}
          <VisuallyHidden> ({t('common.opensNewTab')})</VisuallyHidden>
        </a>
      );
  }

  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (fullLoad || safe.startsWith('#') || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(safe);
  };
  const here = safe.split(/[?#]/)[0] === path;
  return <a href={safe} onClick={handle} aria-current={here ? 'page' : undefined} {...rest}>{children}</a>;
}
